// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Tour/TourForm.jsx (FIXED)

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faTimes } from '@fortawesome/free-solid-svg-icons';
import TextEditor from '../editor_textarea';
import ToastNotification from '../ToastNotification';
import ImageForm from '../ImageForm';
import TourGalleryForm from './TourGalleryForm';
import GalleryHandlers from './GalleryHandlers';
import PDFHandlersTour from './PDFHandlersTour';
import CountryHandlers from './CountryHandlers';
import { API_URLS } from '../../api/api';
import styles from '../../styles/TourForm.module.scss';

const getInitialFormData = () => ({
  travel_ID: undefined,
  user_ID: null,
  websiteID: 3,
  country_ID: [],
  tour_Code: '',
  tour_NamePackage: '',
  Date_Departure: '',
  Date_Return: '',
  Date: new Date().toISOString().split('T')[0],
  Time: '00:00:00',
  tour_Price: 0,
  tour_Tourist: [''],
  tour_ProgramFilePDF: [],
  tour_ProgramDetails: '',
  tour_Conditions: '',
  tour_Information: '',
});

const TourForm = ({ tourData, mode, onCancel, onSave }) => {
  const [formData, setFormData] = useState(getInitialFormData());
  const user_ID = parseInt(localStorage.getItem('user_ID'), 10) || null;

  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const [coverImage, setCoverImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedCoverImageFile, setSelectedCoverImageFile] = useState(null);

  const [pdfList, setPdfList] = useState([]);

  useEffect(() => {
    if (mode === 'edit' && tourData) {
      const processedImages = (tourData.tour_Image ?? []).map(img => ({ ...img, sort: parseInt(img.sort, 10) || 999 })).sort((a, b) => a.sort - b.sort);
      const coverData = processedImages.length > 0 ? processedImages[0] : null;
      const galleryData = processedImages.length > 0 ? processedImages.slice(1) : [];
      const initialCover = coverData ? { ...coverData, url: coverData.resize_url, isFromDatabase: true, sort: 1 } : null;
      const initialGallery = galleryData.map((img, index) => ({ ...img, url: img.resize_url, isFromDatabase: true, sort: index + 2 }));

      // [FIX #1] Initialize pdfList with existing PDFs from tourData
      // This ensures existing PDFs are loaded into the state for display and management.
      const initialPdfs = (tourData.tour_ProgramFilePDF || []).map(pdf => ({
        id: pdf.file_original_url,
        file_original_url: pdf.file_original_url,
        file_resize_url: pdf.file_resize_url || '', // รวม file_resize_url และจัดการกรณี undefined
        isNew: false
      }));
      setPdfList(initialPdfs);

      setFormData({
        ...getInitialFormData(),
        ...tourData,
        tour_Image: [],
        country_ID: tourData.country_ID || [],
        tour_Tourist: tourData.tour_Tourist || [''],
        tour_ProgramFilePDF: [],
        tour_Price: tourData.tour_Price || 0,
      });

      setCoverImage(initialCover);
      setGalleryImages(initialGallery);
      setSelectedCoverImageFile(null);
    } else {
      setFormData(getInitialFormData());
      setCoverImage(null);
      setGalleryImages([]);
      setSelectedCoverImageFile(null);
      setPdfList([]);
    }
  }, [tourData, mode]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      const numericValue = value === '' ? 0 : parseFloat(value);
      setFormData(prev => ({ ...prev, [name]: isNaN(numericValue) ? 0 : numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTouristChange = (index, value) => {
    const newTourists = [...(formData.tour_Tourist || [])];
    newTourists[index] = value;
    setFormData(prev => ({ ...prev, tour_Tourist: newTourists }));
  };
  const addTourist = () => setFormData(prev => ({ ...prev, tour_Tourist: [...(formData.tour_Tourist || []), ''] }));
  const removeTourist = (index) => {
    if (formData.tour_Tourist && formData.tour_Tourist.length > 1) {
      setFormData(prev => ({ ...prev, tour_Tourist: prev.tour_Tourist.filter((_, i) => i !== index) }));
    }
  };

  const handleEditorChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCoverImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedCoverImageFile(file);
      setCoverImage({ url: URL.createObjectURL(file), isFromDatabase: false, fileObject: file, sort: 1 });
    }
  };

  const uploadFiles = async (files, endpoint) => {
    // [FIX #2] Added a check for the endpoint URL to prevent silent failures.
    if (!endpoint) {
      console.error("Upload endpoint is not defined!");
      throw new Error("File upload API endpoint is missing.");
    }
    if (!files || files.length === 0) return null;
    const uploadFormData = new FormData();
    files.forEach(file => uploadFormData.append('uploadFile[]', file));
    try {
      const response = await fetch(endpoint, { method: 'POST', body: uploadFormData });
      if (!response.ok) throw new Error(`File upload failed at ${endpoint} with status ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const newGalleryFilesToUpload = galleryImages.filter(img => !img.isFromDatabase && img.fileObject).map(img => img.fileObject);
      const newPdfsToUpload = pdfList.filter(pdf => pdf.isNew && pdf.fileObject).map(pdf => pdf.fileObject);

      const [coverUploadResult, galleryUploadResult, pdfUploadResult] = await Promise.all([
        uploadFiles(selectedCoverImageFile ? [selectedCoverImageFile] : [], API_URLS.IMAGE_UPLOAD),
        uploadFiles(newGalleryFilesToUpload, API_URLS.IMAGE_UPLOAD),
        uploadFiles(newPdfsToUpload, API_URLS.PDF_UPLOAD_ENDPOINT)
      ]);

      const uploadedGalleryUrlMap = new Map();
      if (galleryUploadResult && galleryUploadResult.originalFileURLs) {
        newGalleryFilesToUpload.forEach((fileObject, index) => {
          uploadedGalleryUrlMap.set(fileObject, {
            original_url: galleryUploadResult.originalFileURLs[index],
            resize_url: galleryUploadResult.resizedFileURLs[index]
          });
        });
      }
      let finalTourImage = [];
      if (coverImage) {
        if (coverImage.isFromDatabase) {
          finalTourImage.push({ original_url: coverImage.original_url, resize_url: coverImage.resize_url, sort: 1 });
        } else if (coverUploadResult && coverUploadResult.originalFileURLs) {
          finalTourImage.push({ original_url: coverUploadResult.originalFileURLs[0], resize_url: coverUploadResult.resizedFileURLs[0], sort: 1 });
        }
      }
      galleryImages.forEach(img => {
        if (img.isFromDatabase) {
          finalTourImage.push({ original_url: img.original_url, resize_url: img.resize_url, sort: img.sort });
        } else if (img.fileObject) {
          const urls = uploadedGalleryUrlMap.get(img.fileObject);
          if (urls) finalTourImage.push({ ...urls, sort: img.sort });
        }
      });

      // This logic was already correct, but it depended on pdfList being properly initialized.
      let finalPDFsToSave = pdfList.filter(pdf => !pdf.isNew).map(({ file_original_url, file_resize_url }) => ({ file_original_url, file_resize_url }));
      if (pdfUploadResult && pdfUploadResult.originalFileURLs) {
        // The API response for PDFs might not have 'resizedFileURLs'. Adjust if necessary.
        const newlyUploadedPdfs = pdfUploadResult.originalFileURLs.map((url, index) => ({
          file_original_url: url,
          // Assuming the PDF API might not return a resized URL. Use the original URL for both if so.
          file_resize_url: pdfUploadResult.resizedFileURLs ? pdfUploadResult.resizedFileURLs[index] : url,
        }));
        finalPDFsToSave = [...finalPDFsToSave, ...newlyUploadedPdfs];
      }

      const dataToSend = {
        ...formData,
        user_ID: user_ID,
        tour_Image: finalTourImage,
        tour_ProgramFilePDF: finalPDFsToSave,
      };

      if (mode === 'edit' && !dataToSend.travel_ID) {
        throw new Error('travel_ID is required for editing.');
      }

      await onSave(dataToSend, mode);
      setToastMessage(`Tour package ${mode === 'add' ? 'created' : 'updated'} successfully!`);
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error during form submission:', error);
      setToastMessage(`Error: ${error.message}. Please try again.`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <ToastNotification showToast={showToast} setShowToast={setShowToast} toastMessage={toastMessage} toastVariant={toastVariant} />
      <form onSubmit={handleSubmit}>
        <div className={`row ${styles.formRow}`}>
          <div className={`col-md-8 ${styles.mainColumn}`}>
            {/* ... other form cards ... */}
            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <div className="mb-3">
                  <label htmlFor="tour_NamePackage" className="form-label">Package Name</label>
                  <input type="text" id="tour_NamePackage" name="tour_NamePackage" className="form-control" value={formData.tour_NamePackage} onChange={handleChange} required />
                </div>
                <div className="row">
                  <div className="col-md-6"><div className="mb-3">
                    <label htmlFor="tour_Code" className="form-label">Tour Code</label>
                    <input type="text" id="tour_Code" name="tour_Code" className="form-control" value={formData.tour_Code} onChange={handleChange} />
                  </div></div>
                  <div className="col-md-6"><div className="mb-3">
                    <label htmlFor="tour_Price" className="form-label">Price (THB)</label>
                    <input type="number" id="tour_Price" name="tour_Price" className="form-control" value={formData.tour_Price} onChange={handleChange} min="0" required />
                  </div></div>
                </div>
              </div>
            </div>

            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <div className="row">
                  <div className="col-md-6"><div className="mb-3">
                    <label htmlFor="Date_Departure" className="form-label">Departure Date</label>
                    <input type="date" id="Date_Departure" name="Date_Departure" className="form-control" value={formData.Date_Departure} onChange={handleChange} />
                  </div></div>
                  <div className="col-md-6"><div className="mb-3">
                    <label htmlFor="Date_Return" className="form-label">Return Date</label>
                    <input type="date" id="Date_Return" name="Date_Return" className="form-control" value={formData.Date_Return} onChange={handleChange} />
                  </div></div>
                </div>
              </div>
            </div>

            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <CountryHandlers country_ID={formData.country_ID} setFormData={setFormData} />
              </div>
            </div>


            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <label className="form-label">Destinations</label>
                {(formData.tour_Tourist || []).map((tourist, index) => (
                  <div key={index} className="input-group mb-2">
                    <input type="text" className="form-control" value={tourist || ''} onChange={(e) => handleTouristChange(index, e.target.value)} placeholder="e.g., Paris, Tokyo" />
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeTourist(index)}>Remove</button>
                  </div>
                ))}
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addTourist}>+ Add Destination</button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Program Details</label>
              <TextEditor value={formData.tour_ProgramDetails}
                onContentChange={(value) => handleEditorChange('tour_ProgramDetails', value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Conditions</label>
              <TextEditor value={formData.tour_Conditions} onContentChange={(value) => handleEditorChange('tour_Conditions', value)} />
            </div>
            <div className="mb-3">
              <label className="form-label">Information</label>
              <TextEditor value={formData.tour_Information} onContentChange={(value) => handleEditorChange('tour_Information', value)} />
            </div>
          </div>

          <div className={`col-md-4 ${styles.sidebarColumn}`}>
            <div className={styles.actionsPanel}>
              <div className={styles.cardBody}>
                <h5 className="card-title">Actions</h5>
                <div className="d-grid gap-2">
                  <button type="submit" className="btn btn-dark" disabled={isSaving}>
                    <FontAwesomeIcon icon={faFloppyDisk} /> {isSaving ? 'Saving...' : (mode === 'edit' ? 'Update Package' : 'Create Package')}
                  </button>
                  <button type="button" onClick={onCancel} className="btn btn-light">
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <h5 className="card-title">Cover Image</h5>
                <ImageForm previewImage={coverImage?.url} handleImageChange={handleCoverImageChange} />
              </div>
            </div>
            <div className={styles.formCard}>
              <div className={styles.cardBody}>
                <h5 className="card-title">Image Gallery</h5>
                <GalleryHandlers galleryImages={galleryImages} setGalleryImages={setGalleryImages}>
                  {({ handleImagesChange, handleImageRemove, handleUpdateSortOrder }) => (
                    <TourGalleryForm
                      previewImages={galleryImages}
                      handleImagesChange={handleImagesChange}
                      handleImageRemove={handleImageRemove}
                      handleUpdateSortOrder={handleUpdateSortOrder}
                    />
                  )}
                </GalleryHandlers>
              </div>
            </div>

            {/* [FIX #3] Removed the unused `initialPDFs` prop. */}
            <PDFHandlersTour
              pdfList={pdfList}
              setPdfList={setPdfList}
              setToastMessage={setToastMessage}
              setToastVariant={setToastVariant}
              setShowToast={setShowToast}
            />
          </div>
        </div>
      </form >
    </div >
  );
};

export default TourForm;